import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Camera, Globe, Hash, Mail, MessageCircle, MessagesSquare, Send, Settings, Smartphone, type LucideIcon } from 'lucide-react';
import { Abas } from '../componentes/abas';
import { SlotAcaoPrimaria } from '../componentes/acao-primaria';
import { AjudaDaTela } from '../componentes/ajuda-tela';
import { chaveDaAba } from '../nucleo/chave-da-tela';
import { classesBotao, RolagemHorizontal } from '../componentes/base';
import { LimiteErro } from '../componentes/limite-erro';
import { SemPermissao, Vazio } from '../componentes/estados';
import { SubtituloPagina } from '../componentes/subtitulo-pagina';
import { useAcessoNavegacao } from '../layout/Shell';
import { BotaoAbrirAoLado } from '../layout/tela-dividida';
import { ABAS_CONFIG_COMUNICACAO, abaVisivel, abasDaNavegacao, comBusca, comunicacaoUnificada, destinoDaAbaProjetada, destinoDoMenu, modulosDoMenu, navegacaoDoMenu, podeVerAba, type Acesso, type AbaNoMenu, type GrupoNoMenu } from '../nucleo/modulos-web';
import { useSessao } from '../nucleo/sessao';
import { rolarAbaAtiva } from '../nucleo/foco';
import { useIcones } from '../nucleo/icones-navegacao';

/**
 * Rola a aba ativa para o centro da barra (celular: 29 de 74 abas ficavam fora da vista); respeita prefers-reduced-motion.
 * r10 visual B9: rola de novo quando o idioma muda (ao vivo ou no 1º acesso, quando o idioma do servidor difere do aparelho:
 * os rótulos mudam de largura), quando a largura da janela muda e quando as fontes terminam de carregar.
 */
function useAbaAtivaVisivel<T extends HTMLElement>(dep: string, idioma: string) {
  const ref = useRef<T>(null);
  const [largura, setLargura] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
  useEffect(() => {
    let quadro = 0;
    const medir = () => {
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(() => setLargura(window.innerWidth));
    };
    window.addEventListener('resize', medir);
    return () => {
      cancelAnimationFrame(quadro);
      window.removeEventListener('resize', medir);
    };
  }, []);
  useEffect(() => {
    let vivo = true;
    const rolar = (suave: boolean) => {
      const ativa = ref.current?.querySelector<HTMLElement>('[aria-current="page"], [aria-selected="true"]');
      if (!vivo || !ativa) return;
      const reduzido = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      // Review r34 visual B1: sem `scrollIntoView` (movia o ponto de partida do Tab para a aba).
      rolarAbaAtiva(ativa, 'centro', suave && !reduzido);
    };
    rolar(true);
    // A largura das abas só é a final com a fonte carregada.
    void document.fonts?.ready.then(() => rolar(false));
    return () => {
      vivo = false;
    };
  }, [dep, idioma, largura]);
  return ref;
}

/** Comunicação unificada (04/11): o ícone de cada canal na barra (sempre à vista, ligado ou não o ícone das outras abas). */
const ICONES_COMUNICACAO: Record<string, LucideIcon> = { chat: MessagesSquare, email: Mail, site: Globe, portal: Smartphone, telegram: Send, slack: Hash, instagram: Camera, whatsapp: MessageCircle };
const iconeDaComunicacao = (chave: string) => {
  const Icone = ICONES_COMUNICACAO[chave];
  return Icone ? <Icone className="size-4 shrink-0" aria-hidden /> : undefined;
};
/** Os canais ainda sem conector, na ordem pedida: depois do APP (posição 40) e antes do Instagram (70). */
const EM_BREVE_COMUNICACAO = [
  { chave: 'telegram', posicao: 50 },
  { chave: 'slack', posicao: 60 },
];
/** Rota extra do Conversas com as regras do número das abas. */
export const CAMINHO_NUMERO_ABAS = '/comunicacao/conversas/numero-das-abas';

/** Fase 2 (W31): rótulo do grupo (aba de primeiro nível): o declarado no núcleo ou o nome do módulo. */
function rotuloDoGrupo(t: (k: string) => string, menu: string, g: GrupoNoMenu) {
  return g.declarado ? t(`navegacao.${menu}.${g.chave}`) : t(`${g.chave}:nome`);
}

/**
 * Review r23 visual (O2): Enter numa aba da barra que leva a OUTRO módulo (a linha do Financeiro junta Contas e Financeiro)
 * remonta a seção enquanto o módulo carrega, e o foco caía no body. A aba ativada pelo teclado pede o foco; a barra que
 * aparece no destino o devolve à aba (vale por 3 s e só no caminho pedido; navegar para outro lugar cancela o pedido).
 */
let focoNaBarra: { para: string; ate: number } | null = null;
export function pedirFocoNaBarra(para: string, agora = Date.now()) {
  focoNaBarra = { para, ate: agora + 3000 };
}
export function focoPendenteDaBarra(pathname: string, agora = Date.now()): string | null {
  const p = focoNaBarra;
  if (!p) return null;
  const caminho = p.para.split('?')[0]!;
  if (agora > p.ate || (pathname !== caminho && !pathname.startsWith(`${caminho}/`))) {
    focoNaBarra = null;
    return null;
  }
  return p.para;
}

/** Barra de abas (sublinhado) que navega: uma aba por destino, com o rótulo do módulo dono. */
function BarraDeAbas({ rotulo, itens, refAbas }: { rotulo: string; itens: { chave: string; para: string; rotulo: string; icone?: ReactNode; selo?: ReactNode }[]; refAbas: RefObject<HTMLUListElement | null> }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const para = focoPendenteDaBarra(pathname);
    if (!para) return;
    const alvo = [...(refAbas.current?.querySelectorAll<HTMLElement>('a[data-para]') ?? [])].find((a) => a.dataset.para === para);
    if (alvo && document.activeElement !== alvo) alvo.focus();
  }, [pathname, refAbas]);
  return (
    <nav aria-label={rotulo}>
      <RolagemHorizontal className="-mx-4 sm:mx-0" classeInterna="px-4 sm:px-0" classeBotoes="top-1/2 -translate-y-1/2">
        <ul ref={refAbas} className="flex min-w-max gap-1 border-b border-borda">
          {itens.map((a) => (
            <li key={a.para}>
              <NavLink
                to={a.para}
                data-aba={a.chave}
                data-para={a.para}
                // Clique sem mouse (Enter ou Espaço no link): `detail` 0.
                onClick={(e) => e.detail === 0 && pedirFocoNaBarra(a.para)}
                className={({ isActive }) =>
                  `-mb-px ${a.icone ? 'flex items-center gap-1.5' : 'block'} border-b-2 px-3 py-2.5 text-sm whitespace-nowrap ${isActive ? 'border-acento-forte font-semibold text-acento-forte' : 'border-transparent font-medium text-tinta-3 hover:text-tinta-1'}`
                }
              >
                {a.icone}
                {a.rotulo}
                {a.selo}
              </NavLink>
            </li>
          ))}
        </ul>
      </RolagemHorizontal>
    </nav>
  );
}

/**
 * F6: kicker, título, botão primário, abas da seção (os submenus vivem aqui) e conteúdo. Fase 2 (W31): o menu pode ser por
 * módulos (abas de módulo em cima, como sempre), por grupos (Cadastros: Cadastros, Serviços, Financeiro, Agenda e Estoque -
 * Produtos, cada grupo com abas de vários módulos) ou de linha única (Financeiro). Os caminhos são sempre
 * `/<menu>/<slug>/<aba>`; só a composição das abas muda (`navegacaoDoMenu`).
 */
export function Secao() {
  const { t, i18n } = useTranslation();
  const { menu = '', modulo = '' } = useParams();
  const s = useSessao();
  // D2: ícones nas abas (ligados por padrão; só o desligar explícito do workspace tira).
  const icones = useIcones();
  // D1 (sub-sistemas): a navegação (barras e destino) segue o sub-sistema atual; as rotas não. A página fora do sub-sistema
  // abre pelo link (atalho, favorito, indicador) com o menu do sub-sistema atual, e aparece na barra enquanto está aberta.
  // U15 F5: o acesso da navegação com sub-sistemas não leva o modo simples; a barra de abas o recebe da sessão.
  const acesso: Acesso = { ...useAcessoNavegacao(), modoSimples: s.modoSimples };
  const todosDoMenu = modulosDoMenu(menu, s);
  const doMenuNav = modulosDoMenu(menu, acesso);
  const nav = navegacaoDoMenu(menu, acesso);
  const atual = todosDoMenu.find((m) => m.slug === modulo);
  const doMenu = atual && !doMenuNav.some((m) => m.slug === atual.slug) ? [...doMenuNav, atual] : doMenuNav;
  const naNavegacao = (chave: string) => acesso.naNavegacao?.(atual?.slug ?? '', chave) ?? true;
  const { pathname, search } = useLocation();
  const refModulos = useAbaAtivaVisivel<HTMLDivElement>(pathname, i18n.language);
  const refAbas = useAbaAtivaVisivel<HTMLUListElement>(pathname, i18n.language);
  // Fase 2 (W6, L1): a rota antiga de uma aba projetada em outro menu leva à nova, com o resto do caminho e a busca
  // (`/financeiro/financeiro/categorias` vira `/cadastros/financeiro/categorias`; link de indicador e favorito seguem valendo).
  // W31: também o módulo que mudou de menu inteiro (`/financeiro/relatorios/dre` vira `/relatorios/relatorios/dre`).
  const projetada = destinoDaAbaProjetada(menu, modulo, pathname.split('/').slice(3).join('/'));
  if (projetada) return <Navigate to={comBusca(projetada, search)} replace />;
  if (!todosDoMenu.length) return <Vazio titulo={t('estados.naoEncontrado')} texto="" />;
  if (!atual) return <Navigate to={comBusca(destinoDoMenu(menu, acesso) ?? destinoDoMenu(menu, s) ?? `/${menu}/${todosDoMenu[0]!.slug}`, search)} replace />;
  // Fase 2 (W8): a navegação mostra só as abas do modo de operação; as rotas aceitam também as permitidas de outro modo
  // (o histórico do fluxo detalhado abre pelo link, só leitura).
  const permitidas = atual.abas.filter((a) => podeVerAba(a, s));
  const visiveis = permitidas.filter((a) => abaVisivel(a, s));
  if (!visiveis.length) return <SemPermissao />;
  // D1: na barra, só as do sub-sistema atual (sem filtro, todas as visíveis, como sempre).
  const doSubsistema = visiveis.filter((a) => naNavegacao(a.chave));
  const abas = doSubsistema.length ? doSubsistema : visiveis;
  const base = `/${menu}/${atual.slug}`;
  const abaAtual = permitidas.find((a) => pathname === `${base}/${a.chave}` || pathname.startsWith(`${base}/${a.chave}/`));
  // Aba de outro modo aberta pelo link (histórico do fluxo detalhado): aparece na barra enquanto está aberta, para a pessoa
  // saber onde está e voltar pela própria barra.
  const abasNaBarra = abaAtual && !abas.includes(abaAtual) ? [...abas, abaAtual] : abas;
  // A primeira aba do módulo NA ORDEM DA NAVEGAÇÃO (posição), destino da raiz `/<menu>/<slug>`.
  const daNavegacao = nav.tipo === 'modulos' ? [] : abasDaNavegacao(nav).filter((x) => x.slug === atual.slug);
  const primeira = daNavegacao[0]?.aba.chave ?? abas[0]!.chave;
  // Comunicação unificada (04/11): barra de canais, ou a do botão Configurar quando uma tela dele está aberta.
  const comunicacao = menu === 'comunicacao' && comunicacaoUnificada();
  const noConfigurar = comunicacao && atual.slug === 'conversas' && (pathname === CAMINHO_NUMERO_ABAS || (!!abaAtual && ABAS_CONFIG_COMUNICACAO.has(abaAtual.chave)));
  const rotuloAba = (slug: string, chave: string) => t(comunicacao && slug === 'conversas' && chave === 'portal' ? 'conversas:abas.app' : `${slug}:abas.${chave}`);
  const extra = (lista: AbaNoMenu[]) => (abaAtual && !lista.some((x) => x.slug === atual.slug && x.aba.chave === abaAtual.chave) ? [...lista, { slug: atual.slug, aba: abaAtual, para: `${base}/${abaAtual.chave}` }] : lista);
  const itensDe = (lista: AbaNoMenu[]) => extra(lista).map((x) => ({ chave: x.aba.chave, para: x.para, rotulo: rotuloAba(x.slug, x.aba.chave), icone: icones.de(`${x.slug}.${x.aba.chave}`) }));
  const itensDaComunicacao = (lista: AbaNoMenu[]) => {
    const canais = lista.map((x) => ({
      chave: x.aba.chave,
      para: x.para,
      rotulo: rotuloAba(x.slug, x.aba.chave),
      icone: iconeDaComunicacao(x.aba.chave),
      selo: x.aba.selo ? <x.aba.selo /> : undefined,
      posicao: x.aba.posicao ?? 1000,
    }));
    const dono = lista[0]?.slug ?? atual.slug;
    const emBreve = EM_BREVE_COMUNICACAO.map((c) => ({ chave: c.chave, para: `/comunicacao/${dono}/em-breve/${c.chave}`, rotulo: t(`comunicacao.emBreve.${c.chave}`), icone: iconeDaComunicacao(c.chave), selo: undefined, posicao: c.posicao }));
    return [...canais, ...emBreve].sort((a, b) => a.posicao - b.posicao);
  };
  const conversasNoMenu = todosDoMenu.find((m) => m.slug === 'conversas');
  const abasDoConfigurar = conversasNoMenu ? conversasNoMenu.abas.filter((a) => ABAS_CONFIG_COMUNICACAO.has(a.chave) && abaVisivel(a, s)) : [];
  const podeNumero = !!conversasNoMenu && s.pode('conversas.configurar');
  const itensDoConfigurar = [
    ...(podeNumero ? [{ chave: 'numero-das-abas', para: CAMINHO_NUMERO_ABAS, rotulo: t('comunicacao.numeroDasAbas') }] : []),
    ...abasDoConfigurar.map((a) => ({ chave: a.chave, para: `/comunicacao/conversas/${a.chave}`, rotulo: rotuloAba('conversas', a.chave), icone: icones.de(`conversas.${a.chave}`) })),
  ];
  // Grupo da aba aberta (menu agrupado): o que tem esta aba deste módulo; na raiz do módulo, o da primeira aba dele.
  const grupoAtual =
    nav.tipo === 'grupos' ? (nav.grupos.find((g) => g.abas.some((x) => x.slug === atual.slug && x.aba.chave === (abaAtual?.chave ?? primeira))) ?? nav.grupos[0]) : undefined;
  const titulo = noConfigurar ? t('comunicacao.configurar') : nav.tipo === 'grupos' && grupoAtual ? rotuloDoGrupo(t, menu, grupoAtual) : nav.tipo === 'linha' ? t(`menus.${menu}`) : t(`${atual.slug}:nome`);

  // U15 F1: caminho da tela para o painel de ajuda (a mesma montagem do `docs/CAMINHOS.md`, no idioma da pessoa): menu, grupo ou
  // módulo (quando há mais de um) e aba.
  const caminhoDaTela = abaAtual
    ? [t(`menus.${menu}`), ...(nav.tipo === 'grupos' || (nav.tipo === 'modulos' && doMenu.length > 1) ? [titulo] : []), rotuloAba(atual.slug, abaAtual.chave)].join(' > ')
    : '';

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t(`menus.${menu}`)}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight break-words">{titulo}</h1>
          {/* Fase 2 (W33, item 5.6): descrição curta da página aberta (aba), pelo mapa central por rota. */}
          {/* Review r25 spec (O1): só na aba que a pessoa pode abrir; "Página não encontrada" (aba sem permissão, como o Histórico telas para quem não é da plataforma) fica sem o subtítulo. */}
          {abaAtual && <SubtituloPagina />}
          {/* As abas não saem no papel; o nome da aba aberta sai (r8 visual M2). */}
          {abaAtual && <p className="mt-1 hidden text-base font-semibold print:block">{rotuloAba(atual.slug, abaAtual.chave)}</p>}
        </div>
        {/* Lote 19 (item 13): "Abrir ao lado" (tela dividida), antes do botão primário da aba. */}
        <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">
          {/* U15 F1: botão Ajuda da aba (painel com a aula da tela) e, na primeira visita, as dicas (F2). */}
          {abaAtual && <AjudaDaTela chave={chaveDaAba(atual, abaAtual)} caminho={caminhoDaTela} />}
          {comunicacao &&
            (noConfigurar ? (
              <Link to="/comunicacao" className={classesBotao('secundario')} data-voltar-comunicacao="">
                <ArrowLeft className="size-4" aria-hidden />
                {t('comunicacao.voltar')}
              </Link>
            ) : (
              itensDoConfigurar[0] && (
                <Link to={itensDoConfigurar[0].para} className={classesBotao('secundario')} data-configurar-comunicacao="">
                  <Settings className="size-4" aria-hidden />
                  {t('comunicacao.configurar')}
                </Link>
              )
            ))}
          <BotaoAbrirAoLado />
          <SlotAcaoPrimaria />
        </div>
      </header>

      {nav.tipo === 'modulos' && doMenu.length > 1 && (
        // Abas de módulo do menu (componente Abas do núcleo): setas, Home e End navegam e o foco fica na aba nova mesmo com a
        // troca de módulo remontando a seção (r6 visual M1 e M2).
        <div ref={refModulos} className="max-w-full">
          <Abas grupo="modulos" rotulo={t(`menus.${menu}`)} ativa={modulo} itens={doMenu.map((m) => ({ chave: m.slug, rotulo: t(`${m.slug}:nome`), para: `/${menu}/${m.slug}`, icone: icones.de(`modulo:${m.slug}`) }))} />
        </div>
      )}
      {nav.tipo === 'grupos' && grupoAtual && (
        // Fase 2 (W31): grupos do menu (as mesmas Abas do núcleo); cada grupo leva à primeira aba dele.
        <div ref={refModulos} className="max-w-full">
          <Abas grupo="grupos" rotulo={t(`menus.${menu}`)} ativa={grupoAtual.chave} itens={nav.grupos.map((g) => ({ chave: g.chave, rotulo: rotuloDoGrupo(t, menu, g), para: g.abas[0]!.para, icone: icones.de(g.declarado ? `grupo:${menu}.${g.chave}` : `modulo:${g.chave}`) }))} />
        </div>
      )}

      {nav.tipo === 'modulos' ? (
        <BarraDeAbas rotulo={t(`${atual.slug}:nome`)} refAbas={refAbas} itens={abasNaBarra.map((a) => ({ chave: a.chave, para: `${base}/${a.chave}`, rotulo: rotuloAba(atual.slug, a.chave), icone: icones.de(`${atual.slug}.${a.chave}`) }))} />
      ) : nav.tipo === 'grupos' ? (
        grupoAtual && <BarraDeAbas rotulo={rotuloDoGrupo(t, menu, grupoAtual)} refAbas={refAbas} itens={itensDe(grupoAtual.abas)} />
      ) : noConfigurar ? (
        <BarraDeAbas rotulo={t('comunicacao.configurar')} refAbas={refAbas} itens={itensDoConfigurar} />
      ) : (
        <BarraDeAbas rotulo={t(`menus.${menu}`)} refAbas={refAbas} itens={comunicacao ? itensDaComunicacao(nav.abas) : itensDe(nav.abas)} />
      )}

      <LimiteErro key={pathname.split('/').slice(0, 4).join('/')}>
        <Routes>
          {/* Review r23 spec (O1): a raiz do módulo leva à primeira aba COM a busca (`/financeiro/relatorios?x` chegava sem `?x`). */}
          <Route index element={<Navigate to={comBusca(primeira, search)} replace />} />
          {permitidas.map((a) => (
            <Route key={a.chave} path={`${a.chave}/*`} element={<a.componente />} />
          ))}
          {atual.rotasExtras?.map((r) => (
            <Route key={r.caminho} path={r.caminho} element={<r.componente />} />
          ))}
          <Route path="*" element={<Vazio titulo={t('estados.naoEncontrado')} texto="" />} />
        </Routes>
      </LimiteErro>
    </div>
  );
}
